import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  AlertCircle, ArrowRight, Calendar, CheckCircle, Clock,
  CreditCard, Inbox, Loader2, Mail, UserPlus, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getStatus = (contact) => contact?.status || 'new';

const STATUS_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  converted: 'Converted',
  archived: 'Archived',
};

const FUNNEL_LABELS = {
  registered: 'Registered',
  level_tested: 'Level tested',
  consultant_requested: 'Needs follow-up',
  awaiting_payment: 'Awaiting payment',
  approved: 'Approved',
  onboarding_done: 'Onboarding done',
  active: 'Active',
  payment_expired: 'Payment expired',
  suspended: 'Suspended',
};

export const AdminLeads = () => {
  const { isStrictAdmin } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeadData = async () => {
      try {
        const [contactsResponse, studentsResponse] = await Promise.all([
          axios.get(`${API_URL}/api/admin/contacts?page_size=200`),
          axios.get(`${API_URL}/api/admin/students?page_size=200`),
        ]);
        setContacts(contactsResponse.data.data ?? contactsResponse.data);
        setStudents(studentsResponse.data.data ?? studentsResponse.data);
      } catch (err) {
        console.error('Failed to load lead overview:', err);
        toast.error(err?.response?.data?.detail || 'Failed to load lead overview');
      } finally {
        setLoading(false);
      }
    };

    fetchLeadData();
  }, []);

  const openContacts = useMemo(
    () => contacts.filter(contact => !['converted', 'archived'].includes(getStatus(contact))),
    [contacts]
  );

  const qualifiedContacts = useMemo(
    () => contacts.filter(contact => getStatus(contact) === 'qualified'),
    [contacts]
  );

  const unreadContacts = useMemo(
    () => contacts.filter(contact => !contact.read),
    [contacts]
  );

  const pipelineStudents = useMemo(
    () => students.filter(student => (
      student.status === 'pending'
      || ['registered', 'level_tested', 'consultant_requested', 'awaiting_payment'].includes(student.funnel_status)
    )),
    [students]
  );

  const workflowSteps = [
    {
      title: 'Lead arrives',
      description: 'Website contact form submissions are captured in Contacts.',
      icon: Inbox,
      href: '/admin/contacts',
      action: 'Open Contacts',
    },
    {
      title: 'Follow up',
      description: 'Reply by email, save notes, and update the lead status.',
      icon: Mail,
      href: '/admin/contacts',
      action: 'Manage follow-up',
    },
    {
      title: 'Convert',
      description: 'Create or link a student account from the contact record.',
      icon: UserPlus,
      href: '/admin/contacts',
      action: 'Convert lead',
    },
    {
      title: 'Plan and schedule',
      description: 'Assign a plan in Students, then schedule classes in Calendar.',
      icon: Calendar,
      href: '/admin/calendar',
      action: 'Open Calendar',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-leads-page">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
            Leads
          </h1>
          <p className="text-[#1A2E6E]/60 mt-1">
            Intake, follow-up, conversion, plan assignment and class scheduling.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/contacts"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors text-sm font-medium"
          >
            <Inbox size={16} />
            Contact Inbox
          </Link>
          <Link
            to="/admin/students"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 transition-colors text-sm font-medium"
          >
            <Users size={16} />
            Students
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-4">
          <p className="text-2xl font-bold text-[#1A2E6E]">{openContacts.length}</p>
          <p className="text-sm text-[#1A2E6E]/60">Open website leads</p>
        </div>
        <div className="bg-[#D72B2B]/5 rounded-xl border border-[#D72B2B]/20 p-4">
          <p className="text-2xl font-bold text-[#D72B2B]">{unreadContacts.length}</p>
          <p className="text-sm text-[#1A2E6E]/60">Unread contacts</p>
        </div>
        <div className="bg-[#F5A623]/5 rounded-xl border border-[#F5A623]/25 p-4">
          <p className="text-2xl font-bold text-[#C97D0A]">{qualifiedContacts.length}</p>
          <p className="text-sm text-[#1A2E6E]/60">Qualified contacts</p>
        </div>
        <div className="bg-[#4CAF50]/5 rounded-xl border border-[#4CAF50]/20 p-4">
          <p className="text-2xl font-bold text-[#4CAF50]">{pipelineStudents.length}</p>
          <p className="text-sm text-[#1A2E6E]/60">Students in pipeline</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        {workflowSteps.map((step, index) => {
          const Icon = step.icon;
          return (
            <div key={step.title} className="bg-white rounded-xl border border-[#E8EAF0] p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#1A2E6E]/8 text-[#1A2E6E] flex items-center justify-center">
                  <Icon size={20} />
                </div>
                <span className="text-xs font-semibold text-[#1A2E6E]/35">
                  Step {index + 1}
                </span>
              </div>
              <h2 className="text-base font-semibold text-[#1A2E6E]">{step.title}</h2>
              <p className="text-sm text-[#1A2E6E]/60 mt-1 min-h-[3.25rem]">{step.description}</p>
              <Link
                to={step.href}
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#D72B2B]"
              >
                {step.action}
                <ArrowRight size={14} />
              </Link>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center gap-2">
            <AlertCircle size={16} className="text-[#F5A623]" />
            <h2 className="text-sm font-semibold text-[#1A2E6E]">Open Contact Leads</h2>
            <span className="ml-auto text-xs bg-[#F5A623]/10 text-[#C97D0A] px-2 py-0.5 rounded-full font-semibold">
              {openContacts.length}
            </span>
          </div>

          {openContacts.length > 0 ? (
            <div className="divide-y divide-[#E8EAF0]">
              {openContacts.slice(0, 8).map(contact => (
                <Link
                  key={contact.id}
                  to="/admin/contacts"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-[#FAFAF7] transition-colors"
                >
                  <div className="w-9 h-9 rounded-full bg-[#1A2E6E]/10 text-[#1A2E6E] flex items-center justify-center font-bold text-sm shrink-0">
                    {contact.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1A2E6E] truncate">{contact.name}</p>
                    <p className="text-xs text-[#1A2E6E]/50 truncate">{contact.email}</p>
                  </div>
                  <span className="text-xs font-semibold text-[#1A2E6E]/60 bg-[#1A2E6E]/5 px-2 py-1 rounded-full shrink-0">
                    {STATUS_LABELS[getStatus(contact)] || 'New'}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-[#4CAF50]/35 mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#1A2E6E]">No open contact leads</p>
              <p className="text-sm text-[#1A2E6E]/55 mt-1">
                New contacts from the public site will appear here.
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center gap-2">
            <Clock size={16} className="text-[#1A2E6E]" />
            <h2 className="text-sm font-semibold text-[#1A2E6E]">Student Pipeline</h2>
            <span className="ml-auto text-xs bg-[#1A2E6E]/10 text-[#1A2E6E] px-2 py-0.5 rounded-full font-semibold">
              {pipelineStudents.length}
            </span>
          </div>

          {pipelineStudents.length > 0 ? (
            <div className="divide-y divide-[#E8EAF0]">
              {pipelineStudents.slice(0, 8).map(student => (
                <Link
                  key={student.id}
                  to={`/admin/students?search=${encodeURIComponent(student.email || student.name || '')}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-[#FAFAF7] transition-colors"
                >
                  <div className="w-9 h-9 rounded-full bg-[#D72B2B]/10 text-[#D72B2B] flex items-center justify-center font-bold text-sm shrink-0">
                    {student.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1A2E6E] truncate">{student.name}</p>
                    <p className="text-xs text-[#1A2E6E]/50 truncate">{student.email}</p>
                  </div>
                  <span className="text-xs font-semibold text-[#1A2E6E]/60 bg-[#1A2E6E]/5 px-2 py-1 rounded-full shrink-0">
                    {FUNNEL_LABELS[student.funnel_status] || student.status}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-[#4CAF50]/35 mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#1A2E6E]">No students waiting in the pipeline</p>
              <p className="text-sm text-[#1A2E6E]/55 mt-1">
                Converted contacts and registrations that need action will appear here.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E8EAF0] p-5">
        <h2 className="text-sm font-semibold text-[#1A2E6E] mb-4">Daily flow</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Link to="/admin/students" className="rounded-xl border border-[#E8EAF0] p-4 hover:bg-[#FAFAF7] transition-colors">
            <Users className="w-5 h-5 text-[#1A2E6E] mb-2" />
            <p className="text-sm font-semibold text-[#1A2E6E]">Approve and assign plan</p>
            <p className="text-xs text-[#1A2E6E]/55 mt-1">Use Students after conversion to approve and link the right plan.</p>
          </Link>
          {isStrictAdmin ? (
            <Link to="/admin/plans" className="rounded-xl border border-[#E8EAF0] p-4 hover:bg-[#FAFAF7] transition-colors">
              <CreditCard className="w-5 h-5 text-[#1A2E6E] mb-2" />
              <p className="text-sm font-semibold text-[#1A2E6E]">Review plans</p>
              <p className="text-xs text-[#1A2E6E]/55 mt-1">Keep packages, credits and pricing available for enrollment.</p>
            </Link>
          ) : (
            <div className="rounded-xl border border-[#E8EAF0] p-4 bg-[#FAFAF7]">
              <CreditCard className="w-5 h-5 text-[#1A2E6E] mb-2" />
              <p className="text-sm font-semibold text-[#1A2E6E]">Plan assignment</p>
              <p className="text-xs text-[#1A2E6E]/55 mt-1">Administrators assign paid plans after the lead is converted.</p>
            </div>
          )}
          <Link to="/admin/calendar" className="rounded-xl border border-[#E8EAF0] p-4 hover:bg-[#FAFAF7] transition-colors">
            <Calendar className="w-5 h-5 text-[#1A2E6E] mb-2" />
            <p className="text-sm font-semibold text-[#1A2E6E]">Schedule class</p>
            <p className="text-xs text-[#1A2E6E]/55 mt-1">Book the first class once the student is approved and has credits.</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLeads;
